import { useState } from "react";

import type {
  AssistantJobResponse,
  FeedbackResponse,
  FeedbackSubmission,
} from "../../shared/api/client";
import { useT, type MessageKey } from "../../shared/i18n";
import {
  Badge,
  Banner,
  Button,
  Card,
  Sheet,
  type BadgeTone,
} from "../../shared/ui";
import { FeedbackBar } from "./FeedbackBar";

const STATUS: Partial<Record<string, { tone: BadgeTone; key: MessageKey }>> = {
  accepted: { tone: "info", key: "assistantJob.state.accepted" },
  queued: { tone: "info", key: "assistantJob.state.queued" },
  running: { tone: "info", key: "assistantJob.state.running" },
  waiting: { tone: "warning", key: "assistantJob.state.waiting" },
  succeeded: { tone: "success", key: "assistantJob.state.succeeded" },
  failed: { tone: "error", key: "assistantJob.state.failed" },
  cancel_requested: {
    tone: "warning",
    key: "assistantJob.state.cancel_requested",
  },
  cancelled: { tone: "neutral", key: "assistantJob.state.cancelled" },
  expired: { tone: "warning", key: "assistantJob.state.expired" },
};

const RETRYABLE = new Set<AssistantJobResponse["state"]>([
  "failed",
  "cancelled",
  "expired",
]);

const CANCELLABLE = new Set<AssistantJobResponse["state"]>([
  "accepted",
  "queued",
  "running",
  "waiting",
]);

export function AssistantJobStatus({
  job,
  onCancel,
  onRetry,
  onFeedback,
}: {
  job: AssistantJobResponse;
  onCancel: () => Promise<void>;
  onRetry: () => Promise<void>;
  /** Present only when the server offers feedback (bootstrap flag). */
  onFeedback?: (body: FeedbackSubmission) => Promise<FeedbackResponse>;
}) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const runtimeState: string = job.state;
  const status = STATUS[runtimeState];
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await action();
    } catch (cause: unknown) {
      setActionError(
        cause instanceof Error
          ? cause.message
          : "Command could not be completed.",
      );
    } finally {
      setBusy(false);
    }
  };
  const result = job.outcome?.message;
  const hasResult = typeof result === "string" && result.trim().length > 0;
  // A Job that failed with a system code has its own retry control; only a
  // reply the owner can actually judge gets the verdict buttons.
  const systemFailure =
    job.state === "failed" &&
    (typeof job.outcome?.code === "string" ||
      typeof job.outcome?.safe_code === "string");
  const rateable =
    onFeedback !== undefined &&
    (job.state === "succeeded" || (job.state === "failed" && !systemFailure));

  return (
    <>
      <section
        aria-label={t("assistantJob.title")}
        className="flex flex-col gap-4 pb-4"
      >
        {actionError && <Banner tone="error">{actionError}</Banner>}
        <Card flush>
          <div className="flex min-h-[52px] items-center justify-between gap-2 border-b border-line-subtle px-4 py-3">
            <span className="text-body text-fg">{t("assistantJob.state")}</span>
            <Badge tone={status?.tone ?? "neutral"}>
              {status ? t(status.key) : runtimeState}
            </Badge>
          </div>
          <div
            className={`flex min-h-[52px] items-center justify-between gap-2 px-4 py-3 ${
              hasResult ? "border-b border-line-subtle" : ""
            }`}
          >
            <span className="text-body text-fg">
              {t("assistantJob.updated")}
            </span>
            <span className="text-callout text-fg-muted">
              {new Date(job.updated_at).toLocaleString()}
            </span>
          </div>
          {hasResult && (
            <p className="px-4 py-3 text-body text-fg">{result.trim()}</p>
          )}
        </Card>
        {rateable && <FeedbackBar key={job.id} onSubmit={onFeedback} />}
        {RETRYABLE.has(job.state) && (
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void run(onRetry);
            }}
          >
            {t("error.retry")}
          </Button>
        )}
        {CANCELLABLE.has(job.state) && (
          <Button
            variant="dangerSoft"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t("assistantJob.cancel")}
          </Button>
        )}
      </section>
      <Sheet
        open={confirming}
        onOpenChange={setConfirming}
        title={t("assistantJob.cancelTitle")}
        description={t("assistantJob.cancelBody")}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t("record.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => {
                void run(async () => {
                  await onCancel();
                  setConfirming(false);
                });
              }}
            >
              {t("assistantJob.cancelConfirm")}
            </Button>
          </>
        }
      />
    </>
  );
}
